import { useMutation } from "@tanstack/react-query";
import { Info, Lock, TimerOff, UserX } from "lucide-react";
import { type FormEvent, useEffect, useId, useRef, useState } from "react";

import { api } from "../api/client";
import type { AuthSession } from "../api/types";
import { attentionSelector, designate } from "../components/attention";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Motion } from "../components/presence";
import { Button, Field, inputClass } from "../components/ui";
import { t } from "../i18n/i18n";
import { LicenceFrame } from "../licence/ActivationScreen";
import { AuthTitle, FormError, PasswordInput } from "./AuthForm";
import type { MountedAccount, OverlayName } from "./authState";
import { authErrorText, authTime } from "./authText";
import { Waiting } from "../components/Waiting";

/** Session verrouillée, expirée ou fermée (docs/17 §4.5, §4.8, §8.3) : surcouche opaque (`bg-canvas`, rien ne
 *  transparaît sur un écran projeté) posée par-dessus l'application montée, à identité fixe. L'identifiant est
 *  affiché, non modifiable : seule la même personne reprend.
 *
 *  - Verrouillée : `POST /api/auth/unlock {password}` sur la session en cours.
 *  - Expirée ou fermée : `POST /api/auth/login` avec `expect_account` = le compte monté.
 *  - « Changer d'utilisateur » : confirmation, puis rechargement complet (saisies gardées sous le nom de la
 *    personne). La confirmation est rendue DANS la surcouche, à la place du formulaire (`SwitchUserPanel`) : une
 *    fenêtre portée dans `body` (z-90) passerait sous la surcouche (z-94) et ne recevrait pas le clavier. */
export function LockOverlay({
  mode,
  identity,
  lockedAt,
  onResumed,
  onSwitch,
}: {
  mode: OverlayName;
  identity: MountedAccount;
  /** Heure du verrouillage (texte de la session verrouillée). */
  lockedAt: string | number | null;
  onResumed: (result: AuthSession) => void;
  onSwitch: () => void;
}) {
  const [password, setPassword] = useState("");
  const [asking, setAsking] = useState(false);
  // Heure figée à l'apparition de la surcouche quand ni le serveur ni ce document ne la connaissent : elle n'avance
  // pas à chaque frappe.
  const [shownAt] = useState(() => Date.now());
  const resume = useMutation({
    mutationFn: () => (mode === "locked" ? api.auth.unlock(password) : api.auth.login(identity.login, password, identity.id)),
    onSuccess: (result) => {
      setPassword("");
      onResumed(result);
    },
    onError: () => setPassword(""),
  });
  const name = identity.display_name;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!password) return void designate(attentionSelector("auth-lock-password"));
    if (!resume.isPending) resume.mutate();
  };
  const title = mode === "locked" ? t("Session verrouillée") : mode === "expired" ? t("Session expirée") : t("Session fermée");
  const text =
    mode === "locked"
      ? t("Session de {nom} ({identifiant}), verrouillée à {heure}. Saisissez votre mot de passe pour reprendre : vos saisies en cours sont intactes.", {
          nom: name,
          identifiant: identity.login,
          heure: authTime(lockedAt ?? shownAt),
        })
      : mode === "expired"
        ? t("La session de {nom} a atteint sa durée maximale de 12 h : saisissez votre mot de passe pour reprendre. Vos saisies en cours sont gardées sur ce poste.", { nom: name })
        : t("La session de {nom} a été fermée (mot de passe changé, trop de mots de passe incorrects, ou par un administrateur de l'unité) : saisissez votre mot de passe pour reprendre. Vos saisies en cours sont gardées sur ce poste.", { nom: name });
  const Icon = mode === "locked" ? Lock : mode === "expired" ? TimerOff : UserX;
  return (
    <LicenceFrame kept>
      <AuthTitle icon={<Icon size={16} />}>{title}</AuthTitle>
      <p className="mt-1.5 text-ui leading-relaxed text-ink-soft">{text}</p>
      {asking ? (
        <SwitchUserPanel
          name={name}
          onConfirm={() => {
            setAsking(false);
            onSwitch();
          }}
          onCancel={() => setAsking(false)}
        />
      ) : (
        <>
          <form onSubmit={submit} className="mt-4 space-y-3">
            {mode !== "locked" && (
              <Field label={t("Identifiant")}>
                <input className={inputClass} value={identity.login} readOnly disabled autoComplete="username" />
              </Field>
            )}
            <Field label={t("Mot de passe de {nom}", { nom: name })}>
              <PasswordInput value={password} onChange={setPassword} current attention="auth-lock-password" autoFocus />
            </Field>
            <FormError message={resume.error ? authErrorText(resume.error, mode === "locked" ? "unlock" : "login") : null} />
            <Button
              type="submit"
              className="w-full"
              disabled={resume.isPending}
              blocked={!password}
              onBlocked={() => designate(attentionSelector("auth-lock-password"))}
            >
              {resume.isPending ? <Waiting>{t("Connexion…")}</Waiting> : mode === "locked" ? t("Déverrouiller") : t("Se connecter")}
            </Button>
          </form>
          <Button variant="link" className="mt-1 py-2.5" onClick={() => setAsking(true)}>
            {mode === "locked" ? t("Changer d'utilisateur") : t("Changer d'utilisateur (Generex se recharge)")}
          </Button>
        </>
      )}
    </LicenceFrame>
  );
}

/** Texte de la confirmation de « Changer d'utilisateur » (§8.3). */
function switchUserText(name: string): string {
  return t("Generex va se recharger. La session de {nom} sera fermée ; ses saisies non enregistrées restent sur ce poste, à son nom.", { nom: name });
}

/** Confirmation de « Changer d'utilisateur » dans la surcouche, à la place du formulaire : « Annuler » a le focus
 *  (Entrée ne confirme pas par mégarde), Échap y revient. L'écouteur est posé sur `window` en capture, après celui du
 *  portail qui arrête Échap pour l'application en dessous (arrêter la propagation n'empêche pas les autres écouteurs
 *  de la même cible). */
export function SwitchUserPanel({ name, onConfirm, onCancel }: { name: string; onConfirm: () => void; onCancel: () => void }) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const cancel = useRef(onCancel);
  cancel.current = onCancel;
  const titleId = useId();
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      cancel.current();
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, []);
  return (
    <Motion origin="fade" role="alertdialog" aria-labelledby={titleId} className="mt-4 rounded-lg border border-line p-4">
      <div className="flex gap-3">
        <span className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
          <Info size={16} />
        </span>
        <div className="min-w-0">
          <h2 id={titleId} className="text-title leading-snug font-semibold text-ink">
            {t("Changer d'utilisateur")}
          </h2>
          <p className="mt-1.5 text-ui leading-relaxed text-ink-soft">{switchUserText(name)}</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <Button ref={cancelRef} variant="secondary" onClick={onCancel}>
          {t("Annuler")}
        </Button>
        <Button onClick={onConfirm}>{t("Changer d'utilisateur")}</Button>
      </div>
    </Motion>
  );
}

/** Confirmation de « Changer d'utilisateur » depuis le menu du compte (§8.3), sur l'application découverte : Generex se
 *  recharge, les saisies restent au nom de la personne. */
export function SwitchUserConfirm({ name, onConfirm, onCancel }: { name: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <ConfirmDialog tone="neutral" title={t("Changer d'utilisateur")} confirmLabel={t("Changer d'utilisateur")} onConfirm={onConfirm} onCancel={onCancel}>
      {switchUserText(name)}
    </ConfirmDialog>
  );
}
